// How a turn's text is drawn: escaped, with **bold**, citations of web sources as
// links, and without its VERDICT/ENDORSE/CONFIDENCE footer (that goes in the chip).

export const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const TAGS = ["VERDICT", "ENDORSE", "CONFIDENCE"];

// "[3]" or "[2, 5]" where `cite(n)` knows source n: each number links to its page.
function linkCitations(html, cite) {
  return html.replace(/\[(\d{1,3}(?:,\s*\d{1,3})*)\]/g, (match, list) => {
    const nums = list.split(",").map((n) => Number(n.trim()));
    const sources = nums.map((n) => cite(n));
    if (!sources.every((s) => s && /^https?:\/\//i.test(s.url))) return match;
    const links = sources.map((s, i) => `<a class="cite" href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(s.title)}">${nums[i]}</a>`);
    return `[${links.join(", ")}]`;
  });
}

// The text a reader sees of a turn: without its tag lines and, while `streaming`, without
// a last line that may be a tag still arriving. The pacer (src/smooth.js) paces this, so
// the hidden lines take no time to "write".
export function visibleText(text, { streaming = false } = {}) {
  let visible = text.replace(/^\s*\**(VERDICT|ENDORSE|CONFIDENCE)\**\s*:.*$/gim, "").trimEnd();
  if (streaming) {
    const nl = visible.lastIndexOf("\n");
    const word = /^\s*\**([A-Z]{2,})\**\s*$/.exec(visible.slice(nl + 1))?.[1];
    if (word && TAGS.some((t) => t.startsWith(word))) visible = visible.slice(0, Math.max(nl, 0)).trimEnd();
  }
  return visible;
}

// `streaming`: the text is still arriving, so its last line may be half a tag
// ("VERD", not yet "VERDICT:") or a bold not yet closed. Drawn as is, those show
// for a moment and then vanish or change shape, which reads as a blip; while
// streaming they are held back until they're complete.
// `cite(n)`: web source n of the session ({ url, title }), or nothing.
export function renderBody(text, { streaming = false, cite = null } = {}) {
  const visible = visibleText(text, { streaming });
  let html = escapeHtml(visible)
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    // *italic*, hugging its words: not "2 * 3 * 4", and not inside a word like 5*3*2.
    .replace(/(^|[^\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, "$1<i>$2</i>");
  if (streaming) html = html.replace(/\*\*(?!.*\*\*)/s, "").replace(/\*$/, "");
  return cite ? linkCitations(html, cite) : html;
}
